import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Storefront, Sparkle, ChartLineUp, Lightning, Package } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border sticky top-0 z-10 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="logo-mark">EK</div>
            <span className="font-display font-semibold text-lg tracking-tight text-navy">EcomKit</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="btn-ghost text-navy">Sign in</Link>
            <Link to="/signup" data-testid="nav-signup" className="btn-primary">Get Started</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-[1.1fr,1fr] gap-14 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 pill pill-neutral mb-6">
              <span className="w-1.5 h-1.5 bg-brand-green rounded-full"/> One catalog · Every marketplace
            </div>
            <h1 className="font-display font-bold text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.02] text-navy">
              Your entire e-commerce business.<br/>
              <span className="text-orange">One dashboard.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              EcomKit connects Amazon, Flipkart, Meesho and Shopify into a single master catalog.
              Find missing listings, complete data with AI, and export anywhere — from one control room.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="cta-signup" className="btn-primary text-base py-3.5 px-6">
                Start free <ArrowRight size={16} weight="bold"/>
              </Link>
              <Link to="/login" data-testid="cta-login" className="btn-secondary text-base py-3.5 px-6">
                Sign in
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              {["1 free marketplace", "AI product completion", "Real-time gap detection", "No credit card"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-brand-green"/> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="relative min-w-0">
            <div className="relative bg-navy rounded-xl overflow-hidden shadow-2xl">
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center gap-2 text-white/60 text-xs font-mono-data">
                  <span className="w-2 h-2 rounded-full bg-red-400"/>
                  <span className="w-2 h-2 rounded-full bg-amber-400"/>
                  <span className="w-2 h-2 rounded-full bg-emerald-400"/>
                  <span className="ml-3">ecomkit / dashboard</span>
                </div>
              </div>
              <div className="p-6 text-white">
                <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Product Availability</div>
                <div className="space-y-2 font-mono-data text-xs">
                  {[
                    { n: "Kaju Premium 500g", s: [1,1,1,1] },
                    { n: "Almond 500g",      s: [1,1,0,0] },
                    { n: "Saffron Pure 2g",  s: [1,0,1,0] },
                    { n: "Walnut Kernels",   s: [0,1,0,0] },
                    { n: "Pistachio Salted", s: [1,1,1,0] },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                      <span className="text-white/90 truncate mr-4">{r.n}</span>
                      <div className="flex gap-1.5 shrink-0">
                        {r.s.map((v, j) => (
                          <span key={j} className={`w-6 h-1.5 rounded-full ${v ? "bg-emerald-400" : "bg-red-400/40"}`}/>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-white/50 tracking-widest">Live</div>
                    <div className="font-display text-xl font-bold text-emerald-400">248</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-white/50 tracking-widest">Missing</div>
                    <div className="font-display text-xl font-bold" style={{color:"hsl(var(--ecom-orange))"}}>37</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <div className="text-[10px] uppercase text-white/50 tracking-widest">Health</div>
                    <div className="font-display text-xl font-bold text-white">86</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -right-4 hidden lg:block card-base p-4 shadow-lg w-56">
              <div className="flex items-center gap-2 text-brand-green mb-1"><Lightning size={14} weight="fill"/> <span className="text-[10px] uppercase tracking-widest font-semibold">AI Autofill</span></div>
              <div className="text-xs font-mono-data text-muted-foreground">bullet_points, SEO, keywords generated in 4.2s</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-border bg-white/50">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">Built for serious sellers</div>
            <h2 className="font-display text-4xl font-bold tracking-tighter text-navy">
              Everything you need to run a multi-marketplace catalog.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {i: Storefront, t: "Connect every marketplace", d: "Amazon, Flipkart, Meesho, Shopify. One central catalog syncs everywhere. Shopify OAuth ready.", tag: "1 platform free"},
              {i: Sparkle, t: "AI product builder", d: "Auto-generate titles, bullets, SEO, alt-text and descriptions. Powered by GPT-5.6 Terra.", tag: "Included"},
              {i: ChartLineUp, t: "Find every missing listing", d: "See exactly which products are missing on which marketplace, and create them in one click.", tag: "Live"},
              {i: Package, t: "Master product database", d: "Every product gets a permanent MPC ID with 30+ editable fields and health scoring.", tag: "MPC-000001"},
              {i: Lightning, t: "Bulk edit at scale", d: "Select 10, 100 or 1,000 products and run AI complete, edit, tag or export in one action.", tag: "Coming"},
              {i: CheckCircle, t: "Health score every product", d: "0–100 completeness score based on data, images, listings and inventory. Improve what matters.", tag: "0-100"},
            ].map((f, i) => (
              <div key={i} className="card-base p-6 group hover:border-navy transition-colors">
                <div className="flex items-start justify-between mb-4">
                  <f.i size={24} weight="duotone" className="text-orange"/>
                  <span className="pill pill-neutral">{f.tag}</span>
                </div>
                <h3 className="font-display font-semibold text-lg text-navy mb-1.5">{f.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="eyebrow mb-3">Simple pricing</div>
            <h2 className="font-display text-4xl font-bold tracking-tighter text-navy">Start free. Pay only for what you scale.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            <div className="card-base p-8">
              <div className="eyebrow mb-2">Free</div>
              <div className="font-display text-5xl font-bold text-navy">₹0<span className="text-lg font-normal text-muted-foreground">/forever</span></div>
              <div className="mt-6 space-y-2 text-sm">
                {["1 marketplace connected","Unlimited products","AI product completion","Import & Export","Product Gap Finder","Health scoring"].map((f)=>(
                  <div key={f} className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-brand-green shrink-0"/> {f}</div>
                ))}
              </div>
              <Link to="/signup" className="btn-secondary w-full justify-center mt-8">Start free</Link>
            </div>
            <div className="card-base p-8 border-2 relative" style={{borderColor:"hsl(var(--ecom-orange))"}}>
              <div className="absolute -top-3 right-6 bg-orange text-white text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full">Scale</div>
              <div className="eyebrow mb-2">Additional Marketplace</div>
              <div className="font-display text-5xl font-bold text-navy">₹500<span className="text-lg font-normal text-muted-foreground">/day</span></div>
              <div className="mt-6 space-y-2 text-sm">
                {["Everything in Free","Additional marketplace access","Product sync across platforms","Cross-marketplace comparison","Priority support"].map((f)=>(
                  <div key={f} className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-brand-green shrink-0"/> {f}</div>
                ))}
              </div>
              <Link to="/signup" className="btn-primary w-full justify-center mt-8">Start free · Upgrade later</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border bg-navy text-white/70">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap justify-between items-center gap-4 text-sm">
          <div className="flex items-center gap-2.5">
            <div className="logo-mark">EK</div>
            <span className="font-display font-semibold text-white">EcomKit</span>
          </div>
          <span className="text-white/50 font-mono-data text-xs">© 2026 EcomKit · Your entire e-commerce business, one dashboard.</span>
        </div>
      </footer>
    </div>
  );
}
